
import { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (!isOpen) {
        setScrolled(window.scrollY > 10);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
      return next;
    });
  };
  const closeMenu = () => setIsOpen(false);
  
  const handleNavigation = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "instant" });
    navigate(href);
  };

  const navItems = [
    { title: 'Home', href: '/' },
    { title: 'About Us', href: '/about' },
    { title: 'Our Team', href: '/team' },
    { title: 'Portfolio', href: '/portfolio' },
    { title: 'Contact Us', href: '/contact' }
  ];

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4 px-6 md:px-10",
        scrolled ? "glass shadow-sm" : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a 
          href="/"
          className="flex items-center space-x-2 text-xl md:text-2xl font-medium"
          onClick={(e) => handleNavigation(e, '/')}
        >
          <span className="bg-primary text-primary-foreground rounded-md p-1 text-base">KT</span>
          <span>Keplerion Tech</span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          {navItems.map(item => (
            <a 
              key={item.title} 
              href={item.href}
              className={cn(
                "text-sm font-medium transition-all relative hover-underline",
                location.pathname === item.href ? "opacity-100" : "opacity-80 hover:opacity-100"
              )}
              onClick={(e) => handleNavigation(e, item.href)}
            >
              {item.title}
              {location.pathname === item.href && (
                <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"></span>
              )}
            </a>
          ))}
          <a 
            href="/contact"
            onClick={(e) => handleNavigation(e, '/contact')}
            className="px-5 py-2 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Book a Call
          </a>
        </nav>

        {/* Mobile Menu Button - Always visible */}
        <button 
          className="md:hidden p-2 -mr-2 text-foreground focus:outline-none z-50 relative"
          onClick={toggleMenu}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div 
        className={cn(
          "md:hidden fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50" 
          onClick={closeMenu}
        />
        
        {/* Menu Panel */}
        <div className="absolute right-0 top-0 bottom-0 w-4/5 max-w-sm h-full bg-background shadow-2xl flex flex-col pt-20">
          {/* Menu items */}
          <nav className="flex-1 overflow-y-auto px-6 py-4">
            {navItems.map(item => (
              <a 
                key={item.title} 
                href={item.href}
                className={cn(
                  "block py-4 text-lg font-medium border-b border-border transition-colors",
                  location.pathname === item.href ? "text-primary" : "hover:text-primary"
                )}
                onClick={(e) => {
                  closeMenu();
                  handleNavigation(e, item.href);
                }}
              >
                {item.title}
              </a>
            ))}
            
            <a 
              href="/contact"
              className="block mt-6 py-3 text-center rounded-full bg-primary text-white text-lg font-medium hover:bg-primary/90 transition-colors"
              onClick={(e) => {
                closeMenu();
                handleNavigation(e, '/contact');
              }}
            >
              Contact Us
            </a>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
